import { api, ApiError } from './client'

/**
 * F-040/F-041 / REQ-032-002. Client for the self-service second-factor endpoints
 * (`AuthController.MfaEnrollStart/MfaEnroll/MfaDisable/MfaStatus`) — everything except the
 * login-time challenge (`api/auth.ts` owns that one, since it runs before a session exists).
 *
 * F-041 added `GET /api/auth/mfa/status` (`AuthController.MfaStatus`), closing the gap F-040's own
 * comment here used to describe: `AccountSecurityPage` now asks for the current user's own status
 * on open instead of always showing both enable and disable actions.
 */

export interface MfaEnrollmentStart {
  secret: string
  otpauthUri: string
  expiresAt: string
}

export interface MfaEnrollmentConfirmed {
  recoveryCodes: string[]
}

/** Response shape of `GET /api/auth/mfa/status` — see `AuthController.MfaStatus`'s doc comment for what each field is and why it is safe to expose. */
export interface MfaStatus {
  active: boolean
  activeSince: string | null
  remainingRecoveryCodes: number
  roleMandateActive: boolean
  setupRequired?: boolean
}

export function getMfaStatus(): Promise<MfaStatus> {
  return api<MfaStatus>('/api/auth/mfa/status')
}

export function startMfaEnrollment(): Promise<MfaEnrollmentStart> {
  return api<MfaEnrollmentStart>('/api/auth/mfa/enroll/start', { method: 'POST' })
}

export function confirmMfaEnrollment(code: string): Promise<MfaEnrollmentConfirmed> {
  return api<MfaEnrollmentConfirmed>('/api/auth/mfa/enroll/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export function disableMfaSelf(code: string): Promise<void> {
  return api<void>('/api/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

/**
 * Backend error codes this card's frontend actually encounters (`MfaAuditActions.Motivations` plus
 * the two ad-hoc strings `AuthController`/`MfaService` return directly), mapped to concrete Italian
 * copy — the brief's explicit "Codice non valido o scaduto", not "Errore 401". Several backend
 * `detail` strings are themselves in English (`MfaService.VerifyLoginChallengeAsync`'s
 * "Invalid code."/"Account temporarily locked. Try again later." — read directly in that file),
 * so falling back to `err.message` for an unrecognised code is a deliberate last resort, not the
 * primary path.
 */
const MFA_ERROR_MESSAGES: Record<string, string> = {
  MFA_INVALID_CODE: 'Codice non valido o scaduto.',
  INVALID_MFA_CODE: 'Codice non valido o scaduto.',
  INVALID_CHALLENGE: 'La verifica non è più valida o è scaduta. Accedi di nuovo.',
  ACCOUNT_LOCKED: 'Account temporaneamente bloccato per troppi tentativi. Riprova tra qualche minuto.',
  ACCOUNT_DEACTIVATED: 'Il tuo account è stato disattivato.',
  MFA_NO_PENDING_ENROLLMENT: 'Nessuna attivazione in corso. Avviane una nuova.',
  MFA_ENROLLMENT_EXPIRED: 'Il tempo per confermare il codice è scaduto. Avvia una nuova attivazione.',
  MFA_ALREADY_ACTIVE: 'Il secondo fattore è già attivo per il tuo account.',
  MFA_NOT_ACTIVE: 'Il secondo fattore non è attivo per il tuo account.',
  MFA_SETUP_REQUIRED: 'Il tuo ruolo richiede il secondo fattore di autenticazione, non ancora configurato per questo account.',
  MFA_REQUIRED_BY_POLICY: 'Il codice di accesso è obbligatorio: per reimpostarlo chiedi a un amministratore.',
  NOT_FOUND: 'Utente non trovato.',
}

/** Translates an error thrown by this module (or `api/auth.ts`'s MFA calls) into concrete Italian copy. */
export function mfaErrorMessage(err: unknown): string {
  const code = err instanceof ApiError ? err.code
    : (err && typeof err === 'object' && 'code' in err && typeof (err as { code?: unknown }).code === 'string')
      ? (err as { code: string }).code
      : undefined

  if (code && code in MFA_ERROR_MESSAGES) return MFA_ERROR_MESSAGES[code]
  return err instanceof Error ? err.message : 'Errore imprevisto. Riprova.'
}
